import React, { ComponentType } from 'react';
import { sortingOrder } from 'utils/sort';

export interface IState {
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
}

export type SortFn =
  | ((prop?: string) => voidPromise)
  | ((prop?: string, sort?: string) => voidPromise);

export interface IComponentProps {
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
  sortBy: (prop: string) => void;
}

function getDisplayName<G extends object>(Component: ComponentType<G>) {
  return Component.displayName || Component.name || 'Component';
}

const withSort = <P extends object>(
  Component: ComponentType<P>,
  defaultSortProp: Nullable<string> = null,
  defaultSortOrder: Nullable<string> = null,
) => {
  class WithSort extends React.Component<
    Omit<P, keyof IComponentProps>,
    IState
  > {
    state = {
      sortProp: defaultSortProp,
      sortOrder: defaultSortOrder,
    };

    getSortOrder = (sortProp: string): SortOrderType => {
      if (sortProp !== this.state.sortProp) {
        return sortingOrder.ASC;
      }
      return this.state.sortOrder === sortingOrder.DESC
        ? sortingOrder.ASC
        : sortingOrder.DESC;
    };

    sortBy: SortFn = (sortProp?: string) => {
      if (!sortProp) return;
      const newSortOrder = this.getSortOrder(sortProp);
      this.setState({
        sortProp: sortProp,
        sortOrder: newSortOrder,
      });
    };

    static readonly displayName = `WithSort(${getDisplayName(Component)})`;

    render() {
      const { sortProp, sortOrder } = this.state;
      const { ...props } = this.props;
      return (
        <Component
          sortProp={sortProp}
          sortOrder={sortOrder}
          sortBy={this.sortBy}
          {...(props as P)}
        />
      );
    }
  }

  return WithSort;
};

export default withSort;
